import React from 'react';
import { motion } from 'framer-motion';
import { Droplet, Sparkles, Flame, Bath, Lock, PartyPopper } from 'lucide-react';
const features = [{
  icon: Bath,
  label: '2 Private Massage Suites'
}, {
  icon: Flame,
  label: 'Jacuzzi Hot Tub Soak'
}, {
  icon: PartyPopper,
  label: 'Event & Party Space'
}, {
  icon: Droplet,
  label: 'Rainfall Shower'
}, {
  icon: Lock,
  label: 'Total Privacy'
}];
export function Facilities() {
  return <section id="facilities" className="py-24 md:py-32 relative" data-id="element-44">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-45">
        <div className="text-center mb-16" data-id="element-46">
          <span className="text-rose-400 uppercase tracking-[0.2em] text-sm mb-4 block" data-id="element-47">
            The Sanctuary
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-lavender-50 mb-4" data-id="element-48">
            Our{' '}
            <span className="italic bg-gradient-to-r from-lavender-300 to-rose-400 bg-clip-text text-transparent" data-id="element-49">
              Facilities
            </span>
          </h2>
          <p className="text-lavender-100/60 font-light max-w-2xl mx-auto" data-id="element-50">
            A discreet, private LA location featuring two intimate massage
            rooms, a deep-soaking jacuzzi, and a flexible event space for
            private gatherings.
          </p>
        </div>

        {/* Feature Pills */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="flex flex-wrap justify-center gap-3 mb-20" data-id="element-51">
          {features.map(feature => <div key={feature.label} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lavender-400/10 border border-lavender-400/20 backdrop-blur-sm" data-id="element-52">
              <feature.icon size={16} className="text-rose-400" data-id="element-53" />
              <span className="text-lavender-100 text-sm font-light" data-id="element-54">
                {feature.label}
              </span>
            </div>)}
        </motion.div>

        {/* Row 1: Two Massage Suites — Blue + Red */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24" data-id="element-55">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-2 lg:order-1" data-id="element-56">
            <div className="grid grid-cols-2 gap-3" data-id="element-57">
              <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-2xl shadow-lavender-500/20" data-id="element-58">
                <img src="https://cdn.magicpatterns.com/uploads/qXoAmhKbpa2LrLGDfaRdjv/Massage_Blue_.jpg" alt="Blue Massage Suite" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-59" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-2xl shadow-rose-500/20 mt-8" data-id="element-60">
                <img src="https://cdn.magicpatterns.com/uploads/o8eHTDte34xNBd4jJtqPgR/Massage_red_.jpg" alt="Red Massage Suite" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-61" />
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-1 lg:order-2" data-id="element-62">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-lavender-500/20 to-rose-500/20 border border-lavender-400/30 mb-6" data-id="element-63">
              <Bath className="text-lavender-300" size={24} strokeWidth={1.2} data-id="element-64" />
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-lavender-50 mb-6" data-id="element-65">
              Two Intimate{' '}
              <span className="italic text-lavender-300" data-id="element-66">Massage Suites</span>
            </h3>
            <p className="text-lavender-100/75 font-light leading-relaxed text-lg mb-6" data-id="element-67">
              Choose your atmosphere — our cool, electric-blue suite or the
              warm, candlelit red room. Each suite is fully private, dressed in
              fresh linens, and reserved exclusively for you during your
              session.
            </p>
            <p className="text-lavender-100/60 font-light leading-relaxed" data-id="element-68">
              Sheets and towels are laundered between every appointment.
              Aromatic oils, fresh florals, and ambient lighting are prepared
              fresh for your arrival.
            </p>
          </motion.div>
        </div>

        {/* Row 2: Jacuzzi */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24" data-id="element-69">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-70">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-lavender-500/20 to-rose-500/20 border border-lavender-400/30 mb-6" data-id="element-71">
              <Flame className="text-rose-300" size={24} strokeWidth={1.2} data-id="element-72" />
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-lavender-50 mb-6" data-id="element-73">
              The <span className="italic text-lavender-300" data-id="element-74">Jacuzzi Soak</span>
            </h3>
            <p className="text-lavender-100/75 font-light leading-relaxed text-lg mb-6" data-id="element-75">
              Sink into our deep, warm jacuzzi — perfect for unwinding before
              your session or melting into bliss afterward. Surrounded by
              twinkling lights and lush greenery, the water is meticulously
              maintained for the ultimate sensual atmosphere.
            </p>
            <ul className="space-y-3 text-lavender-100/80 font-light" data-id="element-76">
              <li className="flex items-center gap-3" data-id="element-77">
                <Sparkles size={16} className="text-rose-400" data-id="element-78" />
                <span data-id="element-79">Pristine, freshly-treated water for every guest</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-80">
                <Sparkles size={16} className="text-rose-400" data-id="element-81" />
                <span data-id="element-82">Aromatic bath salts & essential oils</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-83">
                <Sparkles size={16} className="text-rose-400" data-id="element-84" />
                <span data-id="element-85">Spacious enough for shared experiences</span>
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-86">
            <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl shadow-rose-500/20" data-id="element-87">
              <img src="https://cdn.magicpatterns.com/uploads/f196AirRtjzobu2kVA8dJC/Spa_2_.jpg" alt="Private Jacuzzi" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-88" />
            </div>
          </motion.div>
        </div>

        {/* Row 3: Event & Party Space */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24" data-id="element-89">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-2 lg:order-1" data-id="element-90">
            <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl shadow-lavender-500/20" data-id="element-91">
              <img src="https://cdn.magicpatterns.com/uploads/err7MWUSijX4jU2aUYFJns/Party_Chairs_.jpg" alt="Event & Party Space" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-92" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-1 lg:order-2" data-id="element-93">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-lavender-500/20 to-rose-500/20 border border-lavender-400/30 mb-6" data-id="element-94">
              <PartyPopper className="text-lavender-300" size={24} strokeWidth={1.2} data-id="element-95" />
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-lavender-50 mb-6" data-id="element-96">
              Event &{' '}
              <span className="italic text-lavender-300" data-id="element-97">Party Space</span>
            </h3>
            <p className="text-lavender-100/75 font-light leading-relaxed text-lg mb-6" data-id="element-98">
              A glamorous, lounge-style space wrapped in mirrors, twinkle
              lights, and bold rose decor. Perfect for hosting massage parties,
              bachelorette gatherings, birthdays, and intimate celebrations.
            </p>
            <p className="text-lavender-100/60 font-light leading-relaxed" data-id="element-99">
              Available for private rental — includes tables, covered chairs,
              sound system, and clean-up. 420-friendly. See our{' '}
              <a href="#massage-party" className="text-rose-300 hover:text-rose-200 transition-colors underline underline-offset-4" data-id="element-100">
                Massage Party menu
              </a>{' '}
              for details.
            </p>
          </motion.div>
        </div>

        {/* Host Portrait */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-plum-900/60 to-plum-950/80 border border-lavender-400/20" data-id="element-101">
          <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-102">
            <div className="aspect-[3/4] md:aspect-auto overflow-hidden" data-id="element-103">
              <img src="https://cdn.magicpatterns.com/uploads/48fLLXN7gjkemSp1eDShqK/Yasmine_Spa_Photo_.jpg" alt="Your host at Afriel Spa" className="w-full h-full object-cover" data-id="element-104" />
            </div>
            <div className="p-10 md:p-14 flex flex-col justify-center" data-id="element-105">
              <span className="text-rose-400 uppercase tracking-[0.2em] text-xs mb-4" data-id="element-106">
                Your Host
              </span>
              <h3 className="font-serif text-3xl md:text-4xl text-lavender-50 mb-6" data-id="element-107">
                Warmth, care &{' '}
                <span className="italic text-lavender-300" data-id="element-108">
                  total discretion
                </span>
              </h3>
              <p className="text-lavender-100/75 font-light leading-relaxed text-lg" data-id="element-109">
                Every guest is welcomed personally and made to feel at home from
                the moment they arrive. Your comfort, privacy, and pleasure are
                the heart of the Afriel experience.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}